import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { always, ifElse, isEmpty, isNil, times } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import TrashCan from '@gef-ui/material-icons/TrashCan';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SortCell from 'components/atoms/GefMui/SortCell';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import fetch from 'features/common/fetch';
import { deleteCampaign, loadMore, loadPage, reload, sortRecords } from 'features/views/campaignList/actions';
import { FIELDS } from 'features/views/campaignList/form';
import { getCampaigns, getSort } from 'features/views/campaignList/selectors';
import { fromIsoDate, getCodelistByName, getCodelistPropById, getRoutePath } from 'utils';
import { joinString } from 'utils/string';

const headerData = [
	{ name: FIELDS.campaignName },
	{ name: FIELDS.channel, sortValue: 'channelId' },
	{ name: FIELDS.campaignState, sortValue: 'campaignStateId' },
	{ name: 'startDate' },
	{ name: 'endDate' },
];

export const CampaignListTable = ({ isCampaignSettings, newOrEditCampaignPermission }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const data = useSelector(getCampaigns);
	const sort = useSelector(getSort);
	const channels = useSelector(getCodelistByName(codelists.campaignChannels));
	const states = useSelector(getCodelistByName(codelists.campaignStates));
	const showDeleteCampaignColumn = isCampaignSettings && newOrEditCampaignPermission;

	// render fetchPath according to screen, so pagination will work
	const renderFetchPath = useCallback(() => {
		if (isCampaignSettings) {
			return fetch.constants.urls.campaignListSettings;
		} else {
			return fetch.constants.urls.campaignList;
		}
	}, [isCampaignSettings]);

	const fetchUrl = renderFetchPath();

	const linkPath = ifElse(
		(path) => path.includes('management'),
		always('campaignManagement'),
		always('campaignSettings'),
	)(pathname);

	const handleRedirectToDetail = useCallback(
		(campaignId) => () => {
			const { pathname, state } = getRoutePath(linkPath, { campaignId }, true);
			navigate(pathname, { state });
		},
		[navigate, linkPath],
	);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore(fetchUrl));
	}, [dispatch, fetchUrl]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(page - 1, fetchUrl));
		},
		[dispatch, fetchUrl],
	);

	const handleReload = useCallback(() => {
		dispatch(reload(fetchUrl));
	}, [dispatch, fetchUrl]);

	const handleSort = useCallback(
		(sort) => {
			dispatch(sortRecords(sort, fetchUrl));
		},
		[dispatch, fetchUrl],
	);

	const handleDeleteCampaign = useCallback(
		(campaignId) => () => {
			NiceModal.show(MODALS.lightbox_modal, {
				title: getLabelMessage('campaignsList:lightboxTitleDeleteCampaign'),
				onConfirm: () => dispatch(deleteCampaign(campaignId)),
			});
		},
		[dispatch],
	);

	const renderEmptyRow = useCallback(
		() => (
			<TableRow>
				{times(
					(i) => (
						<TableCell key={i}>---</TableCell>
					),
					5,
				)}
			</TableRow>
		),
		[],
	);

	const hasRecords = !isEmpty(data);

	return (
		<ComponentLoader fetchStorePath={renderFetchPath()} onReload={handleReload}>
			<ComponentLoader fetchStorePath={fetch.constants.urls.campaignDetail}>
				<Table size="small">
					<TableHead>
						<TableRow>
							{headerData.map(({ name, sortValue }, i) => (
								<TableCell key={i}>
									<SortCell
										hasRecords={hasRecords}
										sortBy={sortValue ?? name}
										sort={sort}
										onSort={handleSort}
									>
										{getLabelMessage(`campaignsList:${name}`)}
									</SortCell>
								</TableCell>
							))}
							{showDeleteCampaignColumn && <TableCell />}
						</TableRow>
					</TableHead>
					<TableBody>
						{isNil(data) ? (
							renderEmptyRow()
						) : isEmpty(data) ? (
							<TableHasNoResults />
						) : (
							data.map((item, i) => (
								<TableRow key={i}>
									<TableCell>
										<Link
											sx={{ display: 'flex', width: 'fit-content' }}
											underline="none"
											onClick={handleRedirectToDetail(item?.id)}
										>
											{joinString(' - ', [item?.campaignName, item?.communicationName])}
										</Link>
									</TableCell>
									<TableCell>{getCodelistPropById(item.channelId, channels)}</TableCell>
									<TableCell>{getCodelistPropById(item.campaignStateId, states)}</TableCell>
									<TableCell>{fromIsoDate(item.startDate)}</TableCell>
									<TableCell>{fromIsoDate(item.endDate)}</TableCell>
									{showDeleteCampaignColumn && (
										<TableCell>
											{item?.contactCount === 0 && (
												<IconButton
													id="deleteIcon"
													sx={{ color: 'primary.main', p: 0 }}
													disableRipple
													onClick={handleDeleteCampaign(item.id)}
												>
													<TrashCan sx={{ fontSize: 22 }} />
												</IconButton>
											)}
										</TableCell>
									)}
								</TableRow>
							))
						)}
					</TableBody>
					<PaginationRow
						fetchStorePath={renderFetchPath()}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
						withPagination
					/>
				</Table>
			</ComponentLoader>
		</ComponentLoader>
	);
};

CampaignListTable.propTypes = {
	isCampaignSettings: PropTypes.bool,
	match: PropTypes.object,
	newOrEditCampaignPermission: PropTypes.bool,
};

export default CampaignListTable;
